import React from 'react';
import { motion } from 'framer-motion';
import {
  SparklesIcon,
  TagIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { Article } from '../services/api';
import HeroImage from './HeroImage';

interface ArticleCardProps {
  article: Article;
  ageGroup: '6-9' | '10-13' | '14+';
  onClick: () => void;
  /** true for the first few cards, so their images load without waiting to scroll */
  priority?: boolean;
}

const ArticleCard: React.FC<ArticleCardProps> = ({ article, ageGroup, onClick, priority = false }) => {
  const getCategoryColor = (category: string) => {
    // Age-specific color schemes with progressive maturity
    if (ageGroup === '6-9') {
      // Bright, playful colors for younger kids
      const colors: { [key: string]: string } = {
        'SCIENCE': 'bg-purple-100 text-purple-800 border-purple-300',
        'TECHNOLOGY': 'bg-blue-100 text-blue-800 border-blue-300',
        'EDUCATION': 'bg-sky-100 text-sky-800 border-sky-300',
        'NATURE': 'bg-green-100 text-green-800 border-green-300',
        'ENVIRONMENT': 'bg-emerald-100 text-emerald-800 border-emerald-300',
        'HEALTH': 'bg-rose-100 text-rose-800 border-rose-300',
        'SPORTS': 'bg-green-100 text-green-800 border-green-300',
        'ARTS': 'bg-pink-100 text-pink-800 border-pink-300',
        'CULTURE': 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300',
        'ENTERTAINMENT': 'bg-yellow-100 text-yellow-800 border-yellow-300',
        'HISTORY': 'bg-amber-100 text-amber-800 border-amber-300',
        'POLITICS': 'bg-slate-100 text-slate-800 border-slate-300',
        'ECONOMICS': 'bg-orange-100 text-orange-800 border-orange-300',
        'SOCIETY': 'bg-gray-100 text-gray-800 border-gray-300',
        'FOOD': 'bg-orange-100 text-orange-800 border-orange-300',
        'TRAVEL': 'bg-cyan-100 text-cyan-800 border-cyan-300',
        'TRANSPORTATION': 'bg-zinc-100 text-zinc-800 border-zinc-300',
        'SPACE': 'bg-indigo-100 text-indigo-800 border-indigo-300',
        'INNOVATION': 'bg-teal-100 text-teal-800 border-teal-300',
        'SAFETY': 'bg-red-100 text-red-800 border-red-300',
        'WORLD_NEWS': 'bg-blue-100 text-blue-800 border-blue-300',
        'MISCELLANEOUS': 'bg-gray-100 text-gray-800 border-gray-300',
      };
      return colors[category] || 'bg-gray-100 text-gray-800 border-gray-300';
    } else if (ageGroup === '10-13') {
      // Medium saturation, more mature palette
      const colors: { [key: string]: string } = {
        'SCIENCE': 'bg-purple-200 text-purple-900 border-purple-400',
        'TECHNOLOGY': 'bg-blue-200 text-blue-900 border-blue-400',
        'EDUCATION': 'bg-sky-200 text-sky-900 border-sky-400',
        'NATURE': 'bg-green-200 text-green-900 border-green-400',
        'ENVIRONMENT': 'bg-emerald-200 text-emerald-900 border-emerald-400',
        'HEALTH': 'bg-rose-200 text-rose-900 border-rose-400',
        'SPORTS': 'bg-green-200 text-green-900 border-green-400',
        'ARTS': 'bg-pink-200 text-pink-900 border-pink-400',
        'CULTURE': 'bg-fuchsia-200 text-fuchsia-900 border-fuchsia-400',
        'ENTERTAINMENT': 'bg-yellow-200 text-yellow-900 border-yellow-400',
        'HISTORY': 'bg-amber-200 text-amber-900 border-amber-400',
        'POLITICS': 'bg-slate-200 text-slate-900 border-slate-400',
        'ECONOMICS': 'bg-orange-200 text-orange-900 border-orange-400',
        'SOCIETY': 'bg-gray-200 text-gray-900 border-gray-400',
        'FOOD': 'bg-orange-200 text-orange-900 border-orange-400',
        'TRAVEL': 'bg-cyan-200 text-cyan-900 border-cyan-400',
        'TRANSPORTATION': 'bg-zinc-200 text-zinc-900 border-zinc-400',
        'SPACE': 'bg-indigo-200 text-indigo-900 border-indigo-400',
        'INNOVATION': 'bg-teal-200 text-teal-900 border-teal-400',
        'SAFETY': 'bg-red-200 text-red-900 border-red-400',
        'WORLD_NEWS': 'bg-blue-200 text-blue-900 border-blue-400',
        'MISCELLANEOUS': 'bg-gray-200 text-gray-900 border-gray-400',
      };
      return colors[category] || 'bg-gray-200 text-gray-900 border-gray-400';
    } else {
      // Professional, refined palette for 14+
      const colors: { [key: string]: string } = {
        'SCIENCE': 'bg-purple-300 text-purple-950 border-purple-500',
        'TECHNOLOGY': 'bg-blue-300 text-blue-950 border-blue-500',
        'EDUCATION': 'bg-sky-300 text-sky-950 border-sky-500',
        'NATURE': 'bg-green-300 text-green-950 border-green-500',
        'ENVIRONMENT': 'bg-emerald-300 text-emerald-950 border-emerald-500',
        'HEALTH': 'bg-rose-300 text-rose-950 border-rose-500',
        'SPORTS': 'bg-green-300 text-green-950 border-green-500',
        'ARTS': 'bg-pink-300 text-pink-950 border-pink-500',
        'CULTURE': 'bg-fuchsia-300 text-fuchsia-950 border-fuchsia-500',
        'ENTERTAINMENT': 'bg-yellow-300 text-yellow-950 border-yellow-500',
        'HISTORY': 'bg-amber-300 text-amber-950 border-amber-500',
        'POLITICS': 'bg-slate-300 text-slate-950 border-slate-500',
        'ECONOMICS': 'bg-orange-300 text-orange-950 border-orange-500',
        'SOCIETY': 'bg-gray-300 text-gray-950 border-gray-500',
        'FOOD': 'bg-orange-300 text-orange-950 border-orange-500',
        'TRAVEL': 'bg-cyan-300 text-cyan-950 border-cyan-500',
        'TRANSPORTATION': 'bg-zinc-300 text-zinc-950 border-zinc-500',
        'SPACE': 'bg-indigo-300 text-indigo-950 border-indigo-500',
        'INNOVATION': 'bg-teal-300 text-teal-950 border-teal-500',
        'SAFETY': 'bg-red-300 text-red-950 border-red-500',
        'WORLD_NEWS': 'bg-blue-300 text-blue-950 border-blue-500',
        'MISCELLANEOUS': 'bg-gray-300 text-gray-950 border-gray-500',
      };
      return colors[category] || 'bg-gray-300 text-gray-950 border-gray-500';
    }
  };

  const getCardStyle = () => {
    if (ageGroup === '6-9') {
      return 'bg-gradient-to-br from-yellow-50 via-orange-50 to-pink-50 border-2 border-yellow-200 hover:border-orange-400';
    } else if (ageGroup === '10-13') {
      return 'bg-white border-2 border-gray-100 hover:border-blue-300';
    } else {
      return 'bg-white border-2 border-gray-100 hover:border-indigo-300';
    }
  };

  return (
    <motion.div
      whileHover={{ scale: 1.03, y: -4 }}
      whileTap={{ scale: 0.97 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      onClick={onClick}
      className={`
        ${getCardStyle()}
        rounded-2xl p-6 cursor-pointer transition-all duration-300
        shadow-lg hover:shadow-2xl
        ${ageGroup === '6-9' ? 'transform hover:rotate-1' : ''}
        overflow-hidden backdrop-blur-sm
        h-full flex flex-col
      `}
    >
      {/* Hero image (omitted entirely when there's no image) */}
      <HeroImage
        url={article.hero_image_url}
        alt={article.title}
        priority={priority}
        variant="card"
        className="mb-4"
      />

      {/* Category Badge */}
      <div className="flex items-center justify-between mb-4">
        <span className={`
          px-3 py-1.5 rounded-full text-xs font-bold border-2 shadow-sm
          ${getCategoryColor(article.category)}
          transform hover:scale-105 transition-transform duration-200
        `}>
          <TagIcon className="h-3 w-3 inline mr-1" />
          {article.category.replace('_', ' ')}
        </span>
        {article.emoji_summary && ageGroup === '6-9' && (
          <span className="text-2xl animate-bounce">{article.emoji_summary}</span>
        )}
      </div>

      {/* Title */}
      <h3 className={`
        font-bold mb-3 line-clamp-2 leading-tight
        ${ageGroup === '6-9' ? 'text-xl font-kid text-orange-900' :
          ageGroup === '10-13' ? 'text-lg text-gray-900' :
          'text-lg text-gray-900'}
        hover:text-opacity-80 transition-all duration-200
      `}>
        {article.title}
      </h3>

      {/* Summary */}
      <p className={`
        mb-4 line-clamp-3 leading-relaxed
        ${ageGroup === '6-9' ? 'text-sm text-gray-700 font-medium' : 'text-gray-600 text-sm'}
      `}>
        {article.summary}
      </p>

      {/* Engagement Preview */}
      {article.did_you_know && (
        <div className={`
          mb-4 p-3 rounded-lg border transition-all duration-200
          ${ageGroup === '6-9'
            ? 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100 hover:shadow-sm'
            : 'bg-blue-50 border-blue-200 hover:bg-blue-100 hover:shadow-sm'}
        `}>
          <div className="flex items-start space-x-2">
            <SparklesIcon className={`h-4 w-4 flex-shrink-0 mt-0.5 ${ageGroup === '6-9' ? 'text-yellow-600' : 'text-blue-600'}`} />
            <span className="text-xs font-medium leading-relaxed text-gray-700">
              {article.did_you_know.substring(0, 60)}...
            </span>
          </div>
        </div>
      )}

      {/* Keywords */}
      <div className="flex flex-wrap gap-2 mb-4">
        {article.keywords.slice(0, 3).map((keyword, idx) => (
          <span
            key={idx}
            className="text-xs bg-gray-100 text-gray-700 px-2.5 py-1.5 rounded-md font-medium border border-gray-200 hover:bg-gray-200 hover:shadow-sm transition-all duration-200"
          >
            {keyword}
          </span>
        ))}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end pt-4 mt-auto border-t-2 border-gray-100">
        <motion.button
          whileHover={{ x: 5 }}
          whileTap={{ scale: 0.95 }}
          className={`
            flex items-center space-x-2 font-semibold text-sm
            ${ageGroup === '6-9' ? 'text-orange-600 hover:text-orange-700' :
              ageGroup === '10-13' ? 'text-blue-600 hover:text-blue-700' :
              'text-indigo-600 hover:text-indigo-700'}
            transition-colors duration-200
          `}
        >
          <span>Read More</span>
          <ArrowRightIcon className="h-4 w-4" />
        </motion.button>
      </div>
    </motion.div>
  );
};

export default ArticleCard;